import Route from '@ember/routing/route'
import { service } from '@ember/service'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { restartableTask, timeout } from 'ember-concurrency'
import { Product } from 'teacher-ui-client/utils/products'
import { toRequestScopeStudent } from 'teacher-ui-client/services/reporting'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import type {
  RequestScope,
  BaseReport,
  BaseReportMetaPaging,
  ReportingQueryPage,
  ReportingQuerySort,
} from 'teacher-ui-client/services/reporting'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import type MathseedsPrimeDashboardStudentRoute from 'teacher-ui-client/reporting/ui-scope/mathseeds-prime/dashboard-student/route'
import type { MathseedsPrimeThread } from 'teacher-ui-client/progression-sequence/utils'
import type { NormalizedInterval } from 'date-fns'

interface ProgressOverviewThreadData {
  initial_lesson_completed: Nullable<{ unit_id: number; lesson_id: number }>
  highest_lesson_completed: Nullable<{ unit_id: number; lesson_id: number }>
  average_score: Nullable<number>
  lessons_completed: number
  units_completed: number
}

export interface ProgressOverviewReportData extends BaseReport {
  overview: {
    threads: {
      [MathseedsPrimeThread.Number]: ProgressOverviewThreadData
      [MathseedsPrimeThread.Fractions]: ProgressOverviewThreadData
      [MathseedsPrimeThread.Operations]: ProgressOverviewThreadData
      [MathseedsPrimeThread.Geometry]: ProgressOverviewThreadData
      [MathseedsPrimeThread.Measurement]: ProgressOverviewThreadData
    }
    aggregates: {
      total_units_completed: number
      total_lessons_completed: number
      total_lessons_mastered: number
    }
  }
}

export interface ProgressEventsReportData extends BaseReport {
  events: {
    created_at: ISO8601DateTime
    thread: MathseedsPrimeThread
    unit: number
    lesson: number
    lesson_name: string
    scores: number[] // a score for each attempt, first to last attempt
    number_of_attempts: number
  }[]
  meta: BaseReportMetaPaging
}

export default class ReportingUiScopeMathseedsPrimeDashboardStudentIndexRoute extends Route {
  @service reporting!: ReportingService

  buildRouteInfoMetadata() {
    return {
      exportOptions: [
        {
          exportType: ExportType.CSV,
        },
      ],
    }
  }

  model() {
    const { period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>

    const { student } = this.modelFor(
      'reporting.ui-scope.mathseeds-prime.dashboard-student',
    ) as ModelFor<MathseedsPrimeDashboardStudentRoute>
    const requestScope = toRequestScopeStudent(student.id)

    void this.fetchProgressOverviewReportTask.perform(Product.MSP, period, requestScope).catch(() => {})
    const sort = 'date'
    const page = { number: 1, size: 5 }
    void this.fetchProgressEventsReportTask.perform(Product.MSP, sort, page, period, requestScope).catch(() => {})

    return {
      fetchProgressOverviewReportTask: this.fetchProgressOverviewReportTask,
      fetchProgressEventsReportTask: this.fetchProgressEventsReportTask,
      loadMoreProgressEvents: (currentPageSize: number) => {
        const morePage = { number: 1, size: currentPageSize + 5 }
        void this.fetchProgressEventsReportTask
          .perform(Product.MSP, sort, morePage, period, requestScope)
          .catch(() => {})
      },
    }
  }

  fetchProgressOverviewReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchProgressOverviewReport>) => {
      await timeout(500)
      return this.fetchProgressOverviewReport(...args)
    },
  )

  fetchProgressEventsReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchProgressEventsReport>) => {
      await timeout(500)
      return this.fetchProgressEventsReport(...args)
    },
  )

  fetchProgressOverviewReport(product: Product, period: NormalizedInterval, requestScope: RequestScope) {
    const reportName = 'student-dashboard/lesson-unit-progress/overview'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ period })

    return this.reporting.fetchReport<ProgressOverviewReportData>(url, query)
  }

  fetchProgressEventsReport(
    product: Product,
    sort: ReportingQuerySort,
    page: ReportingQueryPage,
    period: NormalizedInterval,
    requestScope: RequestScope,
  ) {
    const reportName = 'student-dashboard/lesson-unit-progress/events'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ sort, page, period })

    // Curriculum required here, as the events come back with lesson names.
    return this.reporting.fetchReport<ProgressEventsReportData>(url, { ...query, curriculum: 'au' })
  }
}
